import type { ApparatenData } from '@shared/types';

interface Props {
  state: ApparatenData;
  onChange: (data: ApparatenData) => void;
  onNext: () => void;
  onBack: () => void;
  returnToSamenvatting?: boolean;
  onReturnToSamenvatting?: () => void;
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="lc-checkbox">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function RadioGroep<T extends string>({
  name,
  opties,
  waarde,
  onChange,
}: {
  name: string;
  opties: { waarde: T; label: string }[];
  waarde: T | null;
  onChange: (v: T) => void;
}) {
  return (
    <div className="lc-sub-opties">
      {opties.map(({ waarde: w, label }) => (
        <label key={w} className="lc-radio lc-radio--inline">
          <input
            type="radio"
            name={name}
            value={w}
            checked={waarde === w}
            onChange={() => onChange(w)}
          />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
}

export default function StapApparaten({ state, onChange, onNext, onBack, returnToSamenvatting, onReturnToSamenvatting }: Props) {
  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    onNext();
  }

  return (
    <form className="lc-stap lc-stap--apparaten" onSubmit={handleSubmit} noValidate>
      <h2 className="lc-stap__titel">Huidige apparaten</h2>
      <p className="lc-stap__beschrijving">
        Vink aan welke apparaten je in huis hebt. Dit is nodig om de belasting op je meterkast te berekenen.
      </p>

      {/* KOKEN */}
      <section className="lc-apparaten-sectie">
        <h3 className="lc-apparaten-sectie__titel">Koken</h3>

        <Checkbox
          label="Inductiekookplaat"
          checked={state.inductiePlaat}
          onChange={(v) => onChange({ ...state, inductiePlaat: v, inductieVermogen: v ? state.inductieVermogen : null })}
        />
        {state.inductiePlaat && (
          <RadioGroep
            name="inductieVermogen"
            opties={[
              { waarde: '3.7', label: '3,7 kW' },
              { waarde: '7.4', label: '7,4 kW' },
              { waarde: '11', label: '11 kW' },
              { waarde: 'weet_niet', label: 'Weet niet' },
            ]}
            waarde={state.inductieVermogen}
            onChange={(v) => onChange({ ...state, inductieVermogen: v })}
          />
        )}

        <Checkbox
          label="Keramische / elektrische kookplaat"
          checked={state.keramischePlaat}
          onChange={(v) => onChange({ ...state, keramischePlaat: v })}
        />

        <Checkbox
          label="Elektrische oven (los van kookplaat)"
          checked={state.elektrischeOven}
          onChange={(v) => onChange({ ...state, elektrischeOven: v })}
        />
      </section>

      {/* WARM WATER */}
      <section className="lc-apparaten-sectie">
        <h3 className="lc-apparaten-sectie__titel">Warm water</h3>

        <Checkbox
          label="Elektrische boiler"
          checked={state.elektrischeBoiler}
          onChange={(v) => onChange({ ...state, elektrischeBoiler: v, boilerInhoud: v ? state.boilerInhoud : null })}
        />
        {state.elektrischeBoiler && (
          <RadioGroep
            name="boilerInhoud"
            opties={[
              { waarde: '50', label: '50 L' },
              { waarde: '80', label: '80 L' },
              { waarde: '100', label: '100 L' },
              { waarde: '120plus', label: '120 L+' },
            ]}
            waarde={state.boilerInhoud}
            onChange={(v) => onChange({ ...state, boilerInhoud: v })}
          />
        )}
      </section>

      {/* VERWARMING */}
      <section className="lc-apparaten-sectie">
        <h3 className="lc-apparaten-sectie__titel">Verwarming &amp; klimaat</h3>

        <Checkbox
          label="Warmtepomp"
          checked={state.warmtepomp}
          onChange={(v) =>
            onChange({ ...state, warmtepomp: v, warmtepompType: v ? state.warmtepompType : null })
          }
        />
        {state.warmtepomp && (
          <>
            <RadioGroep
              name="warmtepompType"
              opties={[
                { waarde: 'lucht_water', label: 'Lucht-water' },
                { waarde: 'grond_water', label: 'Grond-water' },
                { waarde: 'hybride', label: 'Hybride' },
              ]}
              waarde={state.warmtepompType}
              onChange={(v) => onChange({ ...state, warmtepompType: v })}
            />
            <div className="lc-veld lc-veld--inline">
              <label className="lc-veld__label" htmlFor="lc-wp-vermogen">
                Elektrisch vermogen (kW)
              </label>
              <input
                id="lc-wp-vermogen"
                className="lc-veld__input lc-veld__input--kort"
                type="number"
                min={1}
                max={20}
                placeholder="bijv. 9"
                value={state.warmtepompVermogen ?? ''}
                onChange={(e) =>
                  onChange({ ...state, warmtepompVermogen: parseFloat(e.target.value) || null })
                }
              />
            </div>
          </>
        )}

        <Checkbox
          label="Airconditioning"
          checked={state.airconditioning}
          onChange={(v) => onChange({ ...state, airconditioning: v, aircoUnits: v ? (state.aircoUnits ?? 1) : null })}
        />
        {state.airconditioning && (
          <div className="lc-veld lc-veld--inline">
            <label className="lc-veld__label" htmlFor="lc-airco-units">
              Aantal units
            </label>
            <input
              id="lc-airco-units"
              className="lc-veld__input lc-veld__input--kort"
              type="number"
              min={1}
              max={10}
              value={state.aircoUnits ?? 1}
              onChange={(e) => onChange({ ...state, aircoUnits: parseInt(e.target.value) || 1 })}
            />
          </div>
        )}
      </section>

      {/* WASGOED */}
      <section className="lc-apparaten-sectie">
        <h3 className="lc-apparaten-sectie__titel">Wasgoed</h3>
        <Checkbox label="Wasmachine" checked={state.wasmachine} onChange={(v) => onChange({ ...state, wasmachine: v })} />
        <Checkbox label="Wasdroger (elektrisch)" checked={state.wasdroger} onChange={(v) => onChange({ ...state, wasdroger: v })} />
        <Checkbox label="Vaatwasser" checked={state.vaatwasser} onChange={(v) => onChange({ ...state, vaatwasser: v })} />
      </section>

      {/* OVERIG */}
      <section className="lc-apparaten-sectie">
        <h3 className="lc-apparaten-sectie__titel">Overig</h3>

        <Checkbox
          label="Zonnepanelen"
          checked={state.zonnepanelen}
          onChange={(v) =>
            onChange({ ...state, zonnepanelen: v, zonnepanelenVermogen: v ? state.zonnepanelenVermogen : null })
          }
        />
        {state.zonnepanelen && (
          <div className="lc-veld lc-veld--inline">
            <label className="lc-veld__label" htmlFor="lc-zp-vermogen">
              Totaal vermogen (kWp)
            </label>
            <input
              id="lc-zp-vermogen"
              className="lc-veld__input lc-veld__input--kort"
              type="number"
              min={1}
              max={40}
              placeholder="bijv. 6"
              value={state.zonnepanelenVermogen ?? ''}
              onChange={(e) =>
                onChange({ ...state, zonnepanelenVermogen: parseFloat(e.target.value) || null })
              }
            />
          </div>
        )}

        <Checkbox
          label="Thuisbatterij"
          checked={state.thuisbatterij}
          onChange={(v) =>
            onChange({ ...state, thuisbatterij: v, thuisbatterijCapaciteit: v ? state.thuisbatterijCapaciteit : null })
          }
        />
        {state.thuisbatterij && (
          <RadioGroep
            name="thuisbatterijCapaciteit"
            opties={[
              { waarde: '5', label: '5 kWh' },
              { waarde: '10', label: '10 kWh' },
              { waarde: '15', label: '15 kWh' },
              { waarde: 'anders', label: 'Anders' },
            ]}
            waarde={state.thuisbatterijCapaciteit}
            onChange={(v) => onChange({ ...state, thuisbatterijCapaciteit: v })}
          />
        )}

        <Checkbox label="Sauna" checked={state.sauna} onChange={(v) => onChange({ ...state, sauna: v })} />
        <Checkbox label="Jacuzzi / hottub" checked={state.jacuzzi} onChange={(v) => onChange({ ...state, jacuzzi: v })} />
        <Checkbox label="Zwembad (pomp)" checked={state.zwembad} onChange={(v) => onChange({ ...state, zwembad: v })} />
        <Checkbox label="Thuiskantoor met zware apparatuur" checked={state.thuiskantoor} onChange={(v) => onChange({ ...state, thuiskantoor: v })} />

        <div className="lc-veld">
          <label className="lc-veld__label" htmlFor="lc-opmerkingen">
            Opmerkingen (optioneel)
          </label>
          <textarea
            id="lc-opmerkingen"
            className="lc-veld__textarea"
            maxLength={500}
            rows={3}
            placeholder="Bijv. binnenkort een warmtepomp gepland"
            value={state.opmerkingen}
            onChange={(e) => onChange({ ...state, opmerkingen: e.target.value })}
          />
        </div>
      </section>

      <div className="lc-stap__acties">
        {returnToSamenvatting && (
          <button type="button" className="lc-knop lc-knop--terug-samenvatting" onClick={onReturnToSamenvatting}>
            ← Terug naar samenvatting
          </button>
        )}
        <button type="button" className="lc-knop lc-knop--terug" onClick={onBack}>
          ← Terug
        </button>
        <button type="submit" className="lc-knop lc-knop--primair">
          {returnToSamenvatting ? 'Opslaan & terug →' : 'Volgende →'}
        </button>
      </div>
    </form>
  );
}
